import type { ReactNode, Ref } from 'react';
import { Wordmark } from './Wordmark';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * 🔴 THE FRONT DOOR, ONE FRAME IN EVERY APP — design-system DS-11b2 (§1.7; F372, F377, F552, F255, F570).
 *
 * Every page an app shows before its shell — the sign-in, the back office's forced password change and company picker,
 * the waits for the session — is this frame: the app's `Wordmark` over the card, the card's `h1`, the page, and a help
 * line under it. It replaces the three copies of `Centred` the apps kept line for line (F255).
 *
 * - **Its own `<main>`** (DS-11a1): a signed-out page is not content outside every landmark.
 * - **Transparent** (`.app-centre`, app.css): the ground and its glow show round the card, as they do round the signed-in
 *   chrome; the front door no longer looks like the product before its redesign (F372).
 * - **The help line, on every one of these pages, in the same place** (WCAG 3.2.6 Consistent Help; owner, 2026-10-07:
 *   "Ask an administrator", and the installation's support contact once one can be set). Each app says who to ask: an
 *   administrator of your company, the supplier who issued a dealer's sign-in, whoever runs the installation. Signed in,
 *   the same advice is under Help in the user menu.
 * - **`tools`**: the back office's language toggle, beside the mark (O-8: the portal and the console open in English).
 * - **The tab** says the page (`useDocumentTitle`, DS-11b1): `tab` is its words when the heading's are not them ("Sign
 *   in" for a heading that names the app); `null` leaves the app's own name (the wait for the session).
 *
 * Focus is the page's: each sign-in's first field takes it on load, and the code field when it appears (`autoFocus`,
 * F377). Borrowed by the portal and the console by its own file, never the kit barrel (both `BORROWABLE` lists, C1).
 */
export function SignInFrame({
  word,
  tone,
  title,
  tab,
  titleRef,
  tools,
  help,
  helpLang,
  children,
}: {
  /** The Wordmark's word: "Workbook", "Portal", "System". */
  word: string;
  tone?: 'neutral';
  title: string;
  tab?: string | null;
  /** Makes the heading a focus target (tabIndex -1), for a page that takes over from another (ChooseCompany). */
  titleRef?: Ref<HTMLHeadingElement>;
  tools?: ReactNode;
  help: string;
  /** The help line's language, where the app translates it (the back office's chrome; WCAG 3.1.2). */
  helpLang?: string;
  children: ReactNode;
}) {
  useDocumentTitle(tab === undefined ? title : tab);
  return (
    <main className="app-centre">
      <div className="app-centre__top">
        <span className="app-centre__brand">
          <Wordmark word={word} tone={tone} />
        </span>
        {tools}
      </div>
      <div className="app-centre__card wb-card">
        <div className="wb-card__header">
          <h1 className="wb-card__title" ref={titleRef} tabIndex={titleRef ? -1 : undefined}>
            {title}
          </h1>
        </div>
        <div className="wb-card__body">{children}</div>
      </div>
      <p className="app-centre__help" lang={helpLang}>
        {help}
      </p>
    </main>
  );
}
